/**
 * Engine-internal: derive grouped views from flat V1 state for pool builder / two-stage.
 * Interpretive logic lives here — not in SQL or exported selectors.
 */

import { filterRowsByColumn, filterRowsByColumns } from './rowSelectors.js';
import { getPlayersPerSide } from '../competitiveMatchFormat.js';
import { buildStatusMapFromSessionPlayerRows } from '../matchmakingEligibility.js';

/**
 * Same rule as SQL _session_player_is_matchmaking_orphan / client isOrphanedSessionPlayer.
 * @param {object} row — session_players row
 */
export function isMatchmakingOrphanRow(row) {
  if (!row || row.status !== 'active') return false;
  if (row.player_list_status === 'waitlist') return false;
  if (
    row.court_number != null
    && (row.team_number === 1 || row.team_number === 2)
    && (row.court_type === 'active' || row.court_type === 'next_up')
  ) {
    return false;
  }
  return true;
}

/**
 * @param {object} v1
 * @returns {string[]}
 */
export function buildWaitlistPlayerIds(v1) {
  const rows = v1.state?.session_players || [];
  const waitlistRows = filterRowsByColumn(rows, 'player_list_status', 'waitlist');
  const orphanRows = rows.filter(isMatchmakingOrphanRow);

  const ordered = [
    ...waitlistRows.sort((a, b) => {
      const pa = a.waitlist_position ?? 9999;
      const pb = b.waitlist_position ?? 9999;
      if (pa !== pb) return pa - pb;
      return String(a.player_id).localeCompare(String(b.player_id));
    }),
    ...orphanRows.sort((a, b) => String(a.player_id).localeCompare(String(b.player_id))),
  ];

  const ids = [];
  const seen = new Set();
  ordered.forEach((r) => {
    const id = r.player_id;
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  });
  return ids;
}

/**
 * @param {object} v1
 * @param {string} courtType
 * @returns {Array<{ courtNumber: number, team1: string[], team2: string[] }>}
 */
export function buildCourtAssignments(v1, courtType) {
  const rows = v1.state?.session_players || [];
  const courtNumbers = new Set(
    filterRowsByColumn(rows, 'court_type', courtType).map((r) => Number(r.court_number)),
  );

  (v1.state?.session_courts || [])
    .filter((c) => c.court_type === courtType)
    .forEach((c) => courtNumbers.add(Number(c.court_number)));

  return [...courtNumbers]
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b)
    .map((courtNumber) => {
      const onCourt = filterRowsByColumns(rows, { court_type: courtType, court_number: courtNumber });
      const team1 = onCourt.filter((r) => r.team_number === 1).map((r) => r.player_id);
      const team2 = onCourt.filter((r) => r.team_number === 2).map((r) => r.player_id);
      return { courtNumber, team1, team2 };
    });
}

/**
 * @param {object} v1
 * @returns {Array<{ slotIndex: number, isEmpty: boolean, team1: string[], team2: string[] }>}
 */
export function buildNextUpSlots(v1) {
  const matchFormat = v1.session?.competitive_match_format ?? 'doubles';
  const playersPerSide = getPlayersPerSide(matchFormat);
  const slotCap = v1.session?.upcoming_queue_slot_count ?? 1;
  const courts = buildCourtAssignments(v1, 'next_up');
  const slots = [];

  for (let i = 0; i < slotCap; i += 1) {
    const court = courts.find((c) => c.courtNumber === i) || { courtNumber: i, team1: [], team2: [] };
    const isEmpty = court.team1.length < playersPerSide || court.team2.length < playersPerSide;
    slots.push({
      slotIndex: i,
      isEmpty,
      team1: court.team1,
      team2: court.team2,
    });
  }

  courts.forEach((c) => {
    if (!slots.some((s) => s.slotIndex === c.courtNumber)) {
      const isEmpty = c.team1.length < playersPerSide || c.team2.length < playersPerSide;
      slots.push({ slotIndex: c.courtNumber, isEmpty, team1: c.team1, team2: c.team2 });
    }
  });

  return slots.sort((a, b) => a.slotIndex - b.slotIndex);
}

/**
 * @param {object} v1
 * @param {{ court_type: string, index: number }} target
 */
export function getTargetSlotTeams(v1, target) {
  const rows = filterRowsByColumns(v1.state?.session_players || [], {
    court_type: target.court_type,
    court_number: target.index,
  });
  return {
    team1: rows.filter((r) => r.team_number === 1).map((r) => r.player_id),
    team2: rows.filter((r) => r.team_number === 2).map((r) => r.player_id),
  };
}

/**
 * @param {object} v1
 */
export function getPlayersById(v1) {
  return v1.state?.players || {};
}

/**
 * Status map for matchmaking eligibility (active vs paused/signed_up).
 * @param {object} v1
 * @returns {Record<string, string>}
 */
export function buildSessionPlayerStatusMap(v1) {
  const rows = v1.state?.session_players || [];
  const fromRows = buildStatusMapFromSessionPlayerRows(rows);
  const map = { ...fromRows };
  for (const id of Object.keys(getPlayersById(v1))) {
    if (!map[id]) {
      map[id] = 'active';
    }
  }
  return map;
}
